import './title-lockup.css';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { Children, cloneElement } from 'react';
import { validateSurface } from '../../globals.js';
import '../../_virtual/_tslib.js';
import useViewport from '../../utilities/use-viewport/index.js';
import styles from './title-lockup.module.scss.js';
import { doesChildExist } from './utils/check-child-existence/index.js';
import { getChildrenProps } from './utils/get-children-props/index.js';
import { calculateTextSize } from './utils/calculate-text-size/index.js';
import { calculateEyebrowPadding } from './utils/calculate-eyebrow-padding/index.js';
import TitleLockupEyebrow from './title-lockup-eyebrow.js';
import TitleLockupSubtitle from './title-lockup-subtitle.js';
import TitleLockupTitle from './title-lockup-title.js';

/**
 * @component TitleLockup
 * @package TypeLockups
 * @document ../_docs/code/TitleLockup.editor.tsx
 * @document ../_docs/code/TitleLockup.import.tsx
 * @document ../_docs/code/TitleLockup.snippet.tsx
 */
const TitleLockup = (props) => {
    const { children, eyebrow, title, subtitle, textAlignment = 'left', id, surface: surfaceProp = 'lightPrimary', viewport: viewportProp, } = props;
    const surface = validateSurface(surfaceProp);
    const { eyebrowProps, subtitleProps, titleProps } = getChildrenProps(children);
    const viewport = useViewport('standard', viewportProp);
    const titleLockupWrapperStyles = [styles.titleLockupWrapper];
    if (textAlignment === 'center')
        titleLockupWrapperStyles.push(styles.center);
    const _renderChildren = () => {
        const titleSize = titleProps && titleProps.size;
        const titleBold = titleProps && titleProps.bold === undefined
            ? false
            : titleProps && titleProps.bold;
        const subtitleSize = subtitleProps && subtitleProps.size;
        const eyebrowSize = eyebrowProps && eyebrowProps.size;
        const textSizes = calculateTextSize(titleSize, subtitleSize, eyebrowSize, viewport);
        const eyeBrowPadding = calculateEyebrowPadding(textSizes.titleSize, textSizes.eyebrowSize);
        return Children.map(children, (child) => {
            const childType = child && child.type && child.type.displayName
                ? child.type.displayName
                : '';
            /* istanbul ignore else -- @preserve */
            if (childType === 'TitleLockupSubtitle') {
                return cloneElement(child, Object.assign(Object.assign({}, child.props), { size: textSizes.subtitleSize, surface: surface || validateSurface(child.props.surface), color: child.props.color }));
            }
            /* istanbul ignore else -- @preserve */
            if (childType === 'TitleLockupTitle') {
                return cloneElement(child, Object.assign(Object.assign({}, child.props), { size: textSizes.titleSize, surface: surface || validateSurface(child.props.surface), isStandAlone: !doesChildExist(children, 'TitleLockupSubtitle') }));
            }
            /* istanbul ignore else -- @preserve */
            if (childType === 'TitleLockupEyebrow') {
                return cloneElement(child, Object.assign(Object.assign({}, child.props), { size: textSizes.eyebrowSize, bold: !titleBold || child.props.bold, paddingBottom: eyeBrowPadding, surface: surface || validateSurface(child.props.surface), isStandAlone: !(doesChildExist(children, 'TitleLockupTitle') ||
                        doesChildExist(children, 'TitleLockupSubtitle')) }));
            }
            return child;
        });
    };
    const _renderItemData = () => {
        var _a, _b, _c;
        const titleSize = title && title.size;
        const titleBold = title && title.bold === undefined ? false : title === null || title === void 0 ? void 0 : title.bold;
        const subtitleSize = subtitle && subtitle.size;
        const eyebrowSize = eyebrow && eyebrow.size;
        const eyeBrowBold = eyebrow && eyebrow.bold;
        const textSizes = calculateTextSize(titleSize, subtitleSize, eyebrowSize, viewport);
        const eyeBrowPadding = calculateEyebrowPadding(textSizes.titleSize, textSizes.eyebrowSize);
        return (jsxs(Fragment, { children: [!!eyebrow && (jsx(TitleLockupEyebrow, Object.assign({}, eyebrow, { isStandAlone: !title && !subtitle, paddingBottom: eyeBrowPadding, size: textSizes.eyebrowSize, bold: !titleBold || eyeBrowBold }, (eyebrow.tooltip && {
                    tooltip: Object.assign({}, eyebrow.tooltip),
                }), { surface: (_a = eyebrow === null || eyebrow === void 0 ? void 0 : eyebrow.surface) !== null && _a !== void 0 ? _a : surface, children: eyebrow.children }))), !!title && (jsx(TitleLockupTitle, Object.assign({}, title, (title.tooltip && {
                    tooltip: Object.assign({}, title.tooltip),
                }), { isStandAlone: !subtitle, size: textSizes.titleSize, surface: (_b = title === null || title === void 0 ? void 0 : title.surface) !== null && _b !== void 0 ? _b : surface, children: title.children }))), !!subtitle && (jsx(TitleLockupSubtitle, Object.assign({}, subtitle, { size: textSizes.subtitleSize, color: subtitle.color }, (subtitle.tooltip && {
                    tooltip: Object.assign({}, subtitle.tooltip),
                }), { surface: (_c = subtitle === null || subtitle === void 0 ? void 0 : subtitle.surface) !== null && _c !== void 0 ? _c : surface, children: subtitle.children })))] }));
    };
    return (jsxs("div", { className: titleLockupWrapperStyles.join(' '), id: id, children: [!eyebrow && !title && !subtitle && _renderChildren(), (eyebrow || title || subtitle) && _renderItemData()] }));
};

export { TitleLockup, TitleLockup as default };
